import {
  Apple,
  BookHeart,
  Bot,
  Building2,
  ContactRound,
  Dumbbell,
  Eye,
  Frame,
  HeartPulse,
  Home,
  LayoutDashboard,
  Linkedin,
  MessageCircle,
  Repeat2,
  Settings as SettingsIcon,
  Share2,
  UserCheck,
  Users,
  WalletCards,
} from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BossLogo } from './BossLogo';
import { NavTab } from './NavTab';
import { VoiceControl } from '../VoiceControl';
import { useProduct } from '../../context/ProductContext';

function getUserRole(): string {
  try {
    const raw = localStorage.getItem('boss_user');
    if (!raw) return 'user';
    return JSON.parse(raw).role ?? 'user';
  } catch {
    return 'user';
  }
}

interface NavRailProps {
  collapsed: boolean;
  onCollapseToggle?: () => void;
  onNavClick?: () => void;
  immersive?: boolean;
}

export function NavRail({ collapsed, onCollapseToggle, onNavClick, immersive = false }: NavRailProps) {
  const isAdmin = ['admin', 'owner'].includes(getUserRole());
  const navigate = useNavigate();
  const {
    activeProduct,
    assistantName,
    availableProducts,
    integrationEnabled,
    moduleEnabled,
    setActiveProduct,
  } = useProduct();
  const railBg = immersive
    ? activeProduct === 'hos' ? 'rgba(255, 249, 240, 0.9)' : 'rgba(5, 8, 15, 0.34)'
    : 'var(--v-nav-bg)';
  const combined = availableProducts.length > 1;

  useEffect(() => {
    if (!isAdmin && activeProduct === 'hos' && availableProducts.includes('bos')) {
      setActiveProduct('bos');
    }
  }, [activeProduct, availableProducts, isAdmin, setActiveProduct]);

  const switchProduct = (product: 'bos' | 'hos') => {
    setActiveProduct(product);
    navigate(product === 'hos' ? '/hos' : '/');
    onNavClick?.();
  };

  const moduleOn = (...ids: string[]) => ids.some((id) => moduleEnabled(id));

  return (
    <nav
      className={[
        'boss-nav-surface relative flex flex-col h-full border-r border-border',
        'transition-[width] duration-200',
        immersive ? 'backdrop-blur-xl shadow-2xl' : '',
        collapsed ? 'w-[56px]' : 'w-[212px]',
      ].join(' ')}
      style={{ background: railBg }}
      aria-label="Main navigation"
    >
      <div className="vs-aurora opacity-25 pointer-events-none" aria-hidden />

      <div className="relative flex-shrink-0 border-b border-border">
        <BossLogo collapsed={collapsed} onCollapseToggle={onCollapseToggle} />
        {combined && isAdmin && (
          <div className={collapsed ? 'px-2 pb-2' : 'px-3 pb-3'}>
            {collapsed ? (
              <button
                type="button"
                onClick={() => switchProduct(activeProduct === 'bos' ? 'hos' : 'bos')}
                className="grid h-8 w-full place-items-center rounded-md border border-border bg-surface-2/60 text-text-muted transition-colors hover:text-text-primary"
                title={`Switch to ${activeProduct === 'bos' ? 'HOS' : 'BOS'}`}
                aria-label={`Switch to ${activeProduct === 'bos' ? 'HOS' : 'BOS'}`}
              >
                <Repeat2 className="h-4 w-4" />
              </button>
            ) : (
              <div className="grid grid-cols-2 rounded-lg border border-border bg-surface-2/55 p-1" aria-label="Product switcher">
                {(['bos', 'hos'] as const).map((product) => (
                  <button
                    key={product}
                    type="button"
                    onClick={() => switchProduct(product)}
                    className={`rounded-md px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors ${activeProduct === product ? 'bg-accent/18 text-accent' : 'text-text-muted hover:text-text-primary'}`}
                    aria-pressed={activeProduct === product}
                  >
                    {product}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="relative flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {activeProduct === 'bos' ? (
          <>
            <NavTab to="/" end icon={LayoutDashboard} label="Dashboard" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/office" icon={Building2} label="Office" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/board" icon={Eye} label="Advisory Board" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/client-managers" icon={Users} label="Client Managers" collapsed={collapsed} onNavClick={onNavClick} />

            {!collapsed && (
              <div className="vs-mono px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.22em] text-text-muted">
                Surfaces
              </div>
            )}
            {collapsed && <div className="h-px bg-border mx-2 my-2" aria-hidden />}

            {integrationEnabled('miro') && <NavTab to="/canvas" icon={Frame} label="Canvas" variant="miro" collapsed={collapsed} onNavClick={onNavClick} />}
            <NavTab to="/crm" icon={UserCheck} label="CRM" collapsed={collapsed} onNavClick={onNavClick} />
            {integrationEnabled('linkedin') && <NavTab to="/linkedin" icon={Linkedin} label="LinkedIn" collapsed={collapsed} onNavClick={onNavClick} />}
            {integrationEnabled('whatsapp') && <NavTab to="/whatsapp" icon={MessageCircle} label="WhatsApp" collapsed={collapsed} onNavClick={onNavClick} />}
          </>
        ) : (
          <>
            <NavTab to="/hos" end icon={Home} label="Home" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/hos/assistant" icon={Bot} label={assistantName} collapsed={collapsed} onNavClick={onNavClick} />
            {moduleOn('social') && <NavTab to="/hos/social" icon={Share2} label="Social Media" collapsed={collapsed} onNavClick={onNavClick} />}
            {!combined && moduleOn('social') && integrationEnabled('whatsapp') && <NavTab to="/hos/whatsapp" icon={MessageCircle} label="WhatsApp" collapsed={collapsed} onNavClick={onNavClick} />}
            {moduleOn('address-book') && <NavTab to="/hos/address-book" icon={ContactRound} label="Address Book" collapsed={collapsed} onNavClick={onNavClick} />}

            {!collapsed && (
              <div className="vs-mono px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.22em] text-text-muted">
                Personal guides
              </div>
            )}
            {collapsed && <div className="h-px bg-border mx-2 my-2" aria-hidden />}

            {moduleOn('health', 'hos-health') && <NavTab to="/hos/health" icon={HeartPulse} label="HOS Health" collapsed={collapsed} onNavClick={onNavClick} />}
            {moduleOn('fitness', 'fitness-coach') && <NavTab to="/hos/fitness" icon={Dumbbell} label="Fitness" collapsed={collapsed} onNavClick={onNavClick} />}
            {moduleOn('nutrition', 'nutrition-coach') && <NavTab to="/hos/nutrition" icon={Apple} label="Nutrition" collapsed={collapsed} onNavClick={onNavClick} />}
            {moduleOn('finances', 'finance', 'financial-coach', 'financial-advisor') && <NavTab to="/hos/finances" icon={WalletCards} label="Finances" collapsed={collapsed} onNavClick={onNavClick} />}
            {moduleOn('spiritual', 'spiritual-guide', 'religious') && <NavTab to="/hos/spiritual" icon={BookHeart} label="Spiritual" collapsed={collapsed} onNavClick={onNavClick} />}
          </>
        )}

        {isAdmin && (
          <>
            {!collapsed && (
              <div className="vs-mono px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.22em] text-text-muted">
                Control
              </div>
            )}
            {collapsed && <div className="h-px bg-border mx-2 my-2" aria-hidden />}
            <NavTab to="/settings" icon={SettingsIcon} label="Settings" collapsed={collapsed} onNavClick={onNavClick} />
          </>
        )}
      </div>

      <div className="relative flex-shrink-0 border-t border-border">
        <div className={collapsed ? 'flex flex-col items-center py-2' : 'flex items-center px-3 py-2'}>
          <VoiceControl />
        </div>
      </div>
    </nav>
  );
}
